<script lang="ts">
  // The "trial ended" paywall body, shared by two hosts:
  //  - AdminExpired (the full-screen route reached on a server 402, i.e. EVERY
  //    community the creator holds is expired), and
  //  - AdminShell (the in-panel gate when just the *active* community is expired —
  //    rendered in the content column so the sidebar's community switcher stays
  //    reachable and a creator with other live communities can switch away).
  // The CTA starts a per-community Stripe Checkout and hands the browser off to it.
  import { ApiError } from '../admin';
  import { startCheckout } from '@api';

  // `status` shapes the copy: `expired` = a lapsed subscription (re-subscribe, no
  // fresh trial); `needs_payment` = over the free cap and never subscribed (a fresh
  // over-cap onboard, a grown community, or an added 2nd one) — nothing has ended, so
  // the copy does not say it has.
  let {
    skoolId,
    communityName,
    status = 'expired',
  }: {
    skoolId: number | null;
    communityName: string | null;
    status?: 'expired' | 'needs_payment';
  } = $props();

  const isTrial = $derived(status === 'needs_payment');

  let upgrading = $state(false);
  let error = $state<string | null>(null);

  async function upgrade(): Promise<void> {
    if (skoolId == null || upgrading) return;
    upgrading = true;
    error = null;
    try {
      // `POST /v1/communities/{id}/billing/checkout`. The gate there is `admin`, not
      // `active` — deliberately, because this component only ever renders for a
      // creator every one of whose communities has lapsed, which is exactly the caller
      // `require_active` would answer 402 to.
      const { url } = await startCheckout(skoolId);
      window.location.href = url; // hand off to Stripe's hosted checkout
    } catch (err: unknown) {
      // 503 = Stripe keys not set on the server — a friendly note, never a crash.
      error =
        err instanceof ApiError && err.status === 503
          ? "Billing isn't configured yet — please try again shortly."
          : err instanceof Error
            ? err.message
            : 'Could not start checkout. Please try again.';
      upgrading = false; // stay put so they can retry (on success we navigate away)
    }
  }
</script>

<div class="w-full max-w-md text-center">
  <img src="/icon-128.png" alt="" class="mx-auto mb-5 h-12 w-12 rounded-xl" />
  <div class="eyebrow mb-2">{isTrial ? 'Over the free tier' : 'Trial ended'}</div>
  <h1 class="font-display text-2xl font-bold tracking-tight">
    {#if isTrial}
      <!-- The headline names the community; the plan copy under it does not. -->
      <span class="redact">{communityName ? `${communityName} is over the free tier` : 'This community is over the free tier'}</span>
    {:else}
      <span class="redact">{communityName ? `${communityName}’s free trial has ended` : 'Your free trial has ended'}</span>
    {/if}
  </h1>

  <p class="mx-auto mt-4 max-w-sm text-base-content/70">
    {#if isTrial}
      This community is past the free member limit. Upgrade to keep its Sideguide command center —
      insights, members, moderation, and reactivation. Its data keeps syncing and your members’ AI
      tutor stays live either way.
    {:else}
      Keep this community’s Sideguide command center — insights, members, moderation, and
      reactivation — running. Meanwhile its data keeps syncing and your members’ AI tutor stays live.
    {/if}
  </p>

  <div class="mt-8 flex flex-col items-center gap-3">
    <button
      class="btn btn-primary gap-2 font-semibold"
      onclick={upgrade}
      disabled={upgrading || skoolId == null}
    >
      {#if upgrading}<span class="loading loading-spinner loading-sm"></span>{/if}
      Upgrade this community
    </button>
    {#if error}
      <p class="max-w-sm text-sm text-error/90">{error}</p>
    {/if}
  </div>
</div>
